// FORGED Ed Systems — DEPLOY005 — v0.5.0 — intakeTypes.ts
// Last touched: 2026-05-20
//
// Types for the multi-step intake form. Shared across IntakePage and each
// IntakeStep* component. Mirrors the canonical INTAKE_FIELD_SPEC.md +
// Phase_3_5_intake_spec_addendum.md schemas.
//
// DEPLOY005 — refactored single-bundled CertificationTarget enum
// (e.g. AWS_D1_1_SMAW_2F) into three independent fields:
//   - target_code      (the code/program/standard, e.g. AWS D1.1)
//   - target_process   (the welding process, e.g. SMAW)
//   - target_position  (the position the test runs, e.g. 2F)
// Each is independently selected; many programs target a code with
// process/position deferred to the test administrator.

import type { OperatingMode, EvaluationCurriculum } from "./types";

export type WeldProcessType = "SMAW" | "GMAW" | "GTAW" | "FCAW" | "SAW";

export type MachineEntry = {
  type: WeldProcessType;
  count: number;
  notes: string;
};

export type CalendarBlock = {
  class_period_length_min: number;
  meetings_per_week: number;
  total_term_weeks: number;
};

export type InstructorBackground =
  | "welding_only"
  | "ag_mech_assigned"
  | "industrial_arts"
  | "other_cte_assigned"
  | "new_to_cte"
  | "other";

export type InstructorBlock = {
  background_discipline: InstructorBackground;
  background_other: string;
  years_teaching_welding: number | null;
  years_industry_welding: number | null;
};

export type ExperienceLevels = {
  beginner: number;
  intermediate: number;
  advanced: number;
};

// ---------- Target cert: code + process + position ----------

// The code, program, or standard the program is preparing students for.
// "Code" here means industry/regulatory/certification framework — not a
// programming code.
export type TargetCode =
  | "aws_d1_1"            // AWS D1.1 Structural Steel
  | "aws_d1_2"            // AWS D1.2 Aluminum
  | "aws_d1_3"            // AWS D1.3 Sheet Steel
  | "aws_d1_5"            // AWS D1.5 Bridge Welding
  | "asme_ix"             // ASME Section IX
  | "api_1104"            // API 1104 Pipeline
  | "aws_sense_entry"     // AWS SENSE Entry Level
  | "aws_sense_advanced"  // AWS SENSE Advanced
  | "aws_sense_expert"    // AWS SENSE Expert
  | "nccer_l1"            // NCCER Welding Level 1
  | "nccer_l2"            // NCCER Welding Level 2
  | "nccer_l3"            // NCCER Welding Level 3
  | "awp"                 // American Welding Program
  | "tx_hs_welding_series" // TX HS Welding Series
  | "other";              // Other / Program-specified

// The welding process the cert/test will use. "tbd" is correct when the
// test administrator picks process at test time. "n_a" is for code-/program-
// level targets where process is not specified at the planning stage.
export type TargetProcess =
  | "smaw"
  | "gmaw"
  | "gtaw"
  | "fcaw"
  | "saw"
  | "multiple"
  | "tbd"
  | "n_a";

// The welding position the cert/test will run. F = fillet, G = groove.
export type TargetPosition =
  | "1f" | "2f" | "3f" | "4f"
  | "1g" | "2g" | "3g" | "4g" | "5g" | "6g"
  | "multiple"
  | "tbd"
  | "n_a";

// DEPRECATED in DEPLOY005 — kept for type-import compatibility only.
// All new code uses target_code + target_process + target_position.
export type CertificationTarget = string;

export type CohortBlock = {
  roster_size: number;
  experience_levels: ExperienceLevels;
  target_code: TargetCode;
  target_process: TargetProcess;
  target_position: TargetPosition;
  target_date: string;
};

export type EquipmentBlock = {
  booth_count: number;
  machine_inventory: MachineEntry[];
  gtaw_capable: boolean;
};

export type WeldPositionCapability =
  | "pre_1F"
  | "1F_passing"
  | "2F_passing"
  | "3F_passing"
  | "4F_passing"
  | "1G_passing"
  | "2G_passing"
  | "3G_passing"
  | "4G_passing"
  | "5G_passing"
  | "6G_passing"
  | "mixed_advanced";

export type CohortSegment = {
  segment_label: string;
  student_count: number;
  current_position_capability: WeldPositionCapability;
  current_process_capability: WeldProcessType[];
};

export type BlockerType =
  | "equipment_downtime"
  | "consumables_shortage"
  | "schedule_disruption"
  | "instructor_absence"
  | "facility_issue"
  | "other";

export type NotableBlocker = {
  blocker_type: BlockerType;
  affected_weeks: number;
  description_short: string;
};

export type CohortCurrentState = {
  weeks_remaining_in_term: number;
  cohort_segments: CohortSegment[];
  notable_blockers: NotableBlocker[];
};

// The full multi-step form state.
export type IntakeFormState = {
  // Step 1
  operating_mode: OperatingMode | null;

  // Step 2
  evaluation_curriculum: EvaluationCurriculum | null;

  // Step 3
  instructor: InstructorBlock;
  equipment: EquipmentBlock;
  calendar: CalendarBlock;

  // Step 4
  cohort: CohortBlock;

  // Step 5 (mid_year or cert_prep_window only)
  current_state: CohortCurrentState | null;

  // Step 6 (optional accordion fields stay simple for v1)
  optional_notes: string;
};

export const INITIAL_INTAKE_STATE: IntakeFormState = {
  operating_mode: null,
  evaluation_curriculum: null,
  instructor: {
    background_discipline: "welding_only",
    background_other: "",
    years_teaching_welding: null,
    years_industry_welding: null,
  },
  equipment: {
    booth_count: 6,
    machine_inventory: [{ type: "SMAW", count: 6, notes: "" }],
    gtaw_capable: false,
  },
  calendar: {
    class_period_length_min: 90,
    meetings_per_week: 5,
    total_term_weeks: 36,
  },
  cohort: {
    roster_size: 20,
    experience_levels: { beginner: 0, intermediate: 0, advanced: 0 },
    target_code: "aws_d1_1",
    target_process: "tbd",
    target_position: "tbd",
    target_date: "",
  },
  current_state: null,
  optional_notes: "",
};

// Number of steps visible at any time depends on operating_mode.
// start_of_year skips Step 5; mid_year and cert_prep_window include it.
export function visibleStepCount(mode: OperatingMode | null): number {
  if (mode === "mid_year" || mode === "cert_prep_window") {
    return 6;
  }
  return 5; // start_of_year skips Step 5
}

// ---------- Display labels (single source of truth for UI + agent prompts) ----------

export const TARGET_CODE_LABELS: Record<TargetCode, string> = {
  aws_d1_1: "AWS D1.1 — Structural Steel",
  aws_d1_2: "AWS D1.2 — Aluminum",
  aws_d1_3: "AWS D1.3 — Sheet Steel",
  aws_d1_5: "AWS D1.5 — Bridge Welding",
  asme_ix: "ASME Section IX",
  api_1104: "API 1104 — Pipeline",
  aws_sense_entry: "AWS SENSE — Entry Level",
  aws_sense_advanced: "AWS SENSE — Advanced",
  aws_sense_expert: "AWS SENSE — Expert",
  nccer_l1: "NCCER Welding — Level 1",
  nccer_l2: "NCCER Welding — Level 2",
  nccer_l3: "NCCER Welding — Level 3",
  awp: "American Welding Program (AWP)",
  tx_hs_welding_series: "TX HS Welding Series",
  other: "Other / Program-specified",
};

export const TARGET_PROCESS_LABELS: Record<TargetProcess, string> = {
  smaw: "SMAW (Stick)",
  gmaw: "GMAW (MIG)",
  gtaw: "GTAW (TIG)",
  fcaw: "FCAW (Flux-cored)",
  saw: "SAW (Submerged Arc)",
  multiple: "Multiple processes",
  tbd: "TBD — set by test administrator",
  n_a: "Not applicable",
};

export const TARGET_POSITION_LABELS: Record<TargetPosition, string> = {
  "1f": "1F — Flat fillet",
  "2f": "2F — Horizontal fillet",
  "3f": "3F — Vertical fillet",
  "4f": "4F — Overhead fillet",
  "1g": "1G — Flat groove",
  "2g": "2G — Horizontal groove",
  "3g": "3G — Vertical groove",
  "4g": "4G — Overhead groove",
  "5g": "5G — Horizontal fixed pipe groove",
  "6g": "6G — 45° fixed pipe groove",
  multiple: "Multiple positions",
  tbd: "TBD — set by test administrator",
  n_a: "Not applicable",
};
